import React from "react";

import { Button } from "./components/ui/button";
import { Card } from "./components/ui/card";
import { Dropdown } from "./components/ui/dropdown";
import { cn } from "./lib/utils";

function IconSparkles(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M12 2l1.3 4.7L18 8l-4.7 1.3L12 14l-1.3-4.7L6 8l4.7-1.3L12 2Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M19 13l.8 2.8L22.6 17l-2.8.8L19 20.6l-.8-2.8L15.4 17l2.8-.8L19 13Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        opacity="0.9"
      />
    </svg>
  );
}

function IconTag(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M20 13.5l-6.8 6.8a2 2 0 0 1-2.8 0L3 12.9V4h8.9l8.1 8.1a2 2 0 0 1 0 2.8Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M7.75 7.75h.01"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconClock(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M12 22a10 10 0 1 0-10-10 10 10 0 0 0 10 10Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M12 6v6l4 2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconArrowRight(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M5 12h12"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type HomepageData = {
  headerTitle?: string;
  headerSubtext?: string;
  categories?: Array<{ name?: string; url?: string; isActive?: boolean }>;
  sort?: "newest" | "oldest";
  contentTitle?: string;
  contentHtml?: string;
  cta?: { label?: string; url?: string };
  image?: { url?: string; alt?: string };
  columns?: Array<{ title?: string; text?: string }>;
};

function coerceData(input: unknown): HomepageData {
  if (!input || typeof input !== "object") return {};
  return input as HomepageData;
}

export function HomepageApp({ data }: { data: unknown }) {
  const d = coerceData(data);

  const title = (d.contentTitle ?? "").trim();
  const contentHtml = d.contentHtml ?? "";
  const ctaLabel = (d.cta?.label ?? "").trim();
  const ctaUrl = (d.cta?.url ?? "").trim();
  const imageUrl = (d.image?.url ?? "").trim();
  const imageAlt = (d.image?.alt ?? "").trim();
  const columns = Array.isArray(d.columns) ? d.columns : [];
  const headerTitle = (d.headerTitle ?? "").trim();
  const headerSubtext = (d.headerSubtext ?? "").trim();
  const categories = Array.isArray(d.categories) ? d.categories : [];
  const [sort, setSort] = React.useState<"newest" | "oldest">(d.sort === "oldest" ? "oldest" : "newest");

  const hasHeader = headerTitle || headerSubtext || categories.length > 0;
  const hasMain = title || contentHtml || (ctaLabel && ctaUrl) || imageUrl;
  const hasColumns = columns.some((c) => (c.title ?? "").trim() || (c.text ?? "").trim());

  if (!hasHeader && !hasMain && !hasColumns) return null;

  return (
    <section className="relative px-[5px] pb-12">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-24 top-12 h-64 w-64 rounded-full bg-tbd-sage/20 blur-3xl" />
        <div className="absolute -right-24 top-24 h-64 w-64 rounded-full bg-tbd-almond/18 blur-3xl" />
        <div className="absolute left-1/2 top-[420px] h-72 w-72 -translate-x-1/2 rounded-full bg-tbd-dust/30 blur-3xl" />
      </div>

      <div className="relative mx-auto w-full max-w-none">
        {hasHeader ? (
          <Card className="mb-5 overflow-hidden bg-tbd-porcelain/55">
            <div className="relative p-5 md:px-[clamp(18px,4vw,56px)] md:py-6">
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute -left-10 -top-12 h-48 w-48 rounded-full bg-tbd-sage/18 blur-3xl" />
                <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-tbd-almond/18 blur-3xl" />
              </div>

              <div className="relative flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div>
                  <div className="mb-2 inline-flex items-center gap-2 rounded-[10px] border border-tbd-dust/90 bg-white/50 px-3 py-2 font-[var(--font-sans)] text-[12px] font-semibold tracking-wide text-tbd-charcoal/80">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-[8px] bg-tbd-charcoal text-tbd-porcelain">
                      <IconSparkles className="block h-4 w-4" />
                    </span>
                    Blog
                  </div>

                  {headerTitle ? (
                    <h2 className="m-0 font-[var(--font-hand)] text-[clamp(28px,3vw,44px)] font-semibold leading-[1.05] text-tbd-charcoal">
                      {headerTitle}
                    </h2>
                  ) : null}

                  {headerSubtext ? (
                    <p className="mt-2 max-w-[72ch] font-[var(--font-sans)] text-[15px] leading-7 text-tbd-charcoal/75">
                      {headerSubtext}
                    </p>
                  ) : null}
                </div>

                <div className="flex items-center gap-3">
                  <div className="inline-flex items-center gap-2">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-[10px] border border-black/20 bg-white text-black">
                      <IconClock className="block h-5 w-5 -translate-x-[0.5px] text-black/70" />
                    </span>
                    <Dropdown
                      value={sort}
                      onChange={setSort}
                      options={[
                        { value: "newest", label: "Newest" },
                        { value: "oldest", label: "Oldest" },
                      ]}
                      buttonClassName=""
                    />
                  </div>
                </div>
              </div>

              {categories.length ? (
                <div className="relative mt-4 flex flex-wrap gap-2">
                  {categories.map((c, idx) => {
                    const name = (c.name ?? "").trim();
                    const url = (c.url ?? "").trim();
                    if (!name || !url) return null;
                    const active = !!c.isActive;
                    const accents = [
                      "from-tbd-sage/35 to-tbd-sage/10",
                      "from-tbd-almond/35 to-tbd-almond/10",
                      "from-tbd-dust/45 to-tbd-dust/15"
                    ];
                    const accent = accents[idx % accents.length];
                    return (
                      <a key={idx} href={url} className="group">
                        <span
                          className={cn(
                            "inline-flex items-center gap-2 rounded-[10px] border px-3 py-2 font-[var(--font-sans)] text-[13px] font-semibold transition",
                            "border-tbd-dust/90 bg-gradient-to-b",
                            accent,
                            "hover:brightness-[1.02]",
                            active && "border-tbd-almond/50 from-tbd-almond/45 to-tbd-almond/18"
                          )}
                        >
                          <span
                            className={cn(
                              "h-2 w-2 rounded-full",
                              active ? "bg-tbd-almond" : "bg-tbd-charcoal/30"
                            )}
                          />
                          {name}
                        </span>
                      </a>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </Card>
        ) : null}

        {hasMain ? (
          <Card className="relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute left-0 top-0 h-full w-2 bg-gradient-to-b from-tbd-sage via-tbd-almond to-tbd-dust opacity-70" />
              <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-tbd-sage/18 blur-3xl" />
              <div className="absolute -right-28 bottom-0 h-72 w-72 rounded-full bg-tbd-almond/18 blur-3xl" />
            </div>

            <div className="relative grid gap-6 p-6 md:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] md:gap-10 md:px-[clamp(18px,4vw,56px)] md:py-10">
              <div className="self-center">
                {title ? (
                  <h2 className="mb-3 font-[var(--font-hand)] text-[clamp(28px,3.2vw,48px)] font-semibold leading-[1.05] text-tbd-charcoal">
                    {title}
                  </h2>
                ) : null}

                {contentHtml ? (
                  <div
                    className="max-w-[70ch] font-[var(--font-sans)] text-[15px] leading-7 text-tbd-charcoal/85 [&_p]:m-0 [&_p:not(:last-child)]:mb-3"
                    dangerouslySetInnerHTML={{ __html: contentHtml }}
                  />
                ) : null}

                {ctaLabel && ctaUrl ? (
                  <div className="mt-6">
                    <a href={ctaUrl}>
                      <Button
                        type="button"
                        className="gap-2 bg-gradient-to-b from-tbd-almond to-[#cf8448] shadow-[0_18px_44px_rgba(43,48,44,0.18)] hover:from-tbd-sage hover:to-[#b9b07c]"
                      >
                        {ctaLabel}
                        <IconArrowRight className="h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                ) : null}

                <div className="mt-6 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-[10px] border border-tbd-dust/90 bg-white/50 px-3 py-2 font-[var(--font-sans)] text-[12px] font-semibold text-tbd-charcoal/70">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-[8px] bg-tbd-sage/30 text-tbd-charcoal">
                      <IconTag className="h-4 w-4" />
                    </span>
                    Tips & inspiration
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-[10px] border border-tbd-dust/90 bg-white/50 px-3 py-2 font-[var(--font-sans)] text-[12px] font-semibold text-tbd-charcoal/70">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-[8px] bg-tbd-almond/25 text-tbd-charcoal">
                      <IconClock className="h-4 w-4" />
                    </span>
                    Short & Practical
                  </span>
                </div>
              </div>

              {imageUrl ? (
                <div className="relative aspect-[16/10] overflow-hidden rounded-[10px] border border-tbd-dust/90 bg-tbd-charcoal/5 shadow-[0_22px_60px_rgba(43,48,44,0.18)]">
                  <img
                    src={imageUrl}
                    alt={imageAlt}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-black/10 via-transparent to-white/10" />
                </div>
              ) : null}
            </div>
          </Card>
        ) : null}

        {hasColumns ? (
          <div className="mt-6 grid gap-3 md:grid-cols-2 md:gap-4">
            {columns.map((col, idx) => {
              const colTitle = (col.title ?? "").trim();
              const colText = (col.text ?? "").trim();
              if (!colTitle && !colText) return null;
              const iconBg = idx % 2 === 0 ? "bg-tbd-sage/22" : "bg-tbd-almond/18";
              const iconFg = idx % 2 === 0 ? "text-tbd-charcoal" : "text-tbd-charcoal";
              return (
                <Card key={idx} className="bg-tbd-porcelain/55">
                  <div className="flex gap-4 p-5">
                    <div
                      className={cn(
                        "mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-tbd-dust/90",
                        iconBg,
                        iconFg
                      )}
                    >
                      <IconSparkles className="block h-5 w-5" />
                    </div>
                    <div>
                      {colTitle ? (
                        <h3 className="mb-1 font-[var(--font-hand)] text-[18px] font-semibold text-tbd-charcoal">
                          {colTitle}
                        </h3>
                      ) : null}
                      {colText ? (
                        <p className="m-0 font-[var(--font-sans)] text-[15px] leading-7 text-tbd-charcoal/80">
                          {colText}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : null}
      </div>
    </section>
  );
}
